// health-api/2 - the GET /api/health response, mirrored field-for-field from api/lib/health/types.ts (Lane AM contract,
// Lane AO part-2 fields, and the G001 Lane AR DESIGN D5 additions: `grid`, band.recovered / recovered_entries /
// not_watched, verdict RECOVERED). `rows` and `unattributed` are gone: the page draws the model's dashboard grid.
// The page RENDERS this and evaluates nothing: every state, verdict, colour, role, section, cause, status line and the
// headline was computed server-side. The only state -> style mapping on the client lives in ./present.ts.
//
// Interface names carry a `Health` prefix so they cannot shadow the DOM's global `Node` type.

export type HealthState = "MEASURED" | "UNKNOWN" | "NOT_APPLICABLE";
export type HealthVerdict = "OK" | "BAD" | "INFO" | "RECOVERED";
export type HealthColour = "green" | "red" | "amber" | "grey" | "info";

/** Which band count an entry lands in. Absent or anything else is "data". */
export type HealthSection = "data" | "monitoring" | "housekeeping";

/** Why an UNKNOWN entry or tile could not be established, as a fixed key and its words. */
export interface HealthUnknownCause {
  key: string;
  label: string;
}

export interface HealthCoverage {
  measured?: number;
  expected?: number;
  note?: string;
}

export interface HealthTile {
  /** the monitor the tile belongs to, as shown (registry id, or the redacted unregistered store key); null for store.self */
  monitor: string | null;
  check: string;
  /** the record's node: the canonical model node id when it names one (aliases resolved), else the redacted text */
  node: string | null;
  state: HealthState;
  verdict: HealthVerdict | null;
  colour: HealthColour;
  reason: string | null;
  measured_at_utc: string | null;
  age_h: number | null;
  value: unknown;
  coverage: HealthCoverage | null;
  severity: string | null;
  legacy: boolean;
  failure_class: string | null;
  /** set on every UNKNOWN tile, else null */
  unknown_cause: HealthUnknownCause | null;
  /** MEASURED/BAD tiles only - the first failing write of the current streak, else null */
  failing_since_utc: string | null;
  /** true when that streak reaches back to the start of the store's recorded history */
  failing_since_is_floor: boolean;
  /** the store row's run_id */
  run_id: string | null;
  /** DESIGN-R2 A4: set on a check in grid.unplaced - why it landed on no asset (or was moved off a NOT_APPLICABLE one) */
  unplaced_reason?: string | null;
  /** R4 D2: set only on a grid.unplaced item that is a per-pipeline READING moved off a NOT_APPLICABLE asset (not a check):
   *  the asset it was about. reconciliation.unplaced_evidence counts these. */
  evidence_of?: string | null;
}

export interface HealthMonitor {
  id: string;
  store_key: string | null;
  trigger: "schedule" | "push" | "none";
  cadence_h: number | null;
  cron: string | null;
  publishes: boolean;
  last_write_utc: string | null;
  age_h: number | null;
  /** any tile legacy */
  legacy: boolean;
  coverage_note: string | null;
  tiles: HealthTile[];
}

export type HealthRole = "primary" | "consequent" | "possibly_consequent" | "ok" | "unknown" | "not_applicable";

/** A topology-model node, evaluated over the tiles attributed to it by node (kept for coverage/detail; the page draws
 *  grid assets, not nodes). It carries NO band role (D5). */
export interface HealthNode {
  id: string;
  label: string;
  kind: string;
  env: string | string[] | null;
  lifecycle: string;
  state: string;
  verdict: string | null;
  colour: string;
  reason: string | null;
  measured_at_utc: string | null;
  failure_class: string | null;
  hidden: boolean;
  monitors: string[];
  freshness_contract_text: string | null;
  tiles: HealthTile[];
  section: HealthSection;
  unknown_cause: HealthUnknownCause | null;
  failing_since_utc: string | null;
  failing_since_is_floor: boolean;
}

export interface HealthStoreEntry {
  monitor: string;
  last_write_utc: string | null;
  age_h: number | null;
  trigger: string;
  cadence_h: number | null;
}

/** One line of the UNKNOWN-by-cause breakdown; the counts sum to band.unknown. */
export interface HealthUnknownCauseCount {
  key: string;
  label: string;
  count: number;
  entries: string[];
}

// ------------------------------------------------------------------------------ the dashboard grid (D5) --
export type GridLineKind = "ok" | "problem" | "recovered" | "unknown" | "na" | "none";

/** One status line of an asset (schedule / ran / complete / moved), from the checks its `signals` CITE. */
export interface HealthGridLine {
  word: string;
  kind: GridLineKind;
  reason: string | null;
  cites: string[];
  measured_at_utc: string | null;
}

export interface HealthRecorded {
  text: string;
  tag: string;
  at: string | null;
}

export type GridEnv = "PROD" | "DEV" | "GLOBAL";
export const GRID_ENVS: readonly GridEnv[] = ["PROD", "DEV", "GLOBAL"];
export type GridPlaneId = "dataflow" | "control";

/** DESIGN-R2 A2 / DESIGN-R3 C2: evidence a per-pipeline cite contributed to THIS asset's state (not a check: never drawn
 *  as a check tile, never counted in the reconciliation's landings). */
export interface HealthGridEvidence {
  /** the status line the cite is on ("ran", "complete", "moved") */
  line: string;
  /** the reading's word on that line ("overdue", "recovered", "not observable", ...) */
  word: string;
  /** problem -> MEASURED/BAD evidence; recovered -> MEASURED/RECOVERED evidence; unknown -> UNKNOWN evidence (DESIGN-R3
   *  C2: the pipeline's run cannot be read - "<pipeline>: its run cannot be read from <monitor>/<check> - <why>") */
  kind: "problem" | "recovered" | "unknown";
  /** "<pipeline>: <the reading's reason>" */
  reason: string | null;
  /** the cite, as the lines show it: "<monitor>/<check> (pipeline <P>)" */
  cites: string[];
}

/** DESIGN-R2 A7: how the rows read become what the page draws. check_tiles = store_rows - excluded_rows + made_by_api
 *  (the rows of page-excluded monitors are read but never drawn; the store's own self-check is not a check tile);
 *  balanced = landed + unplaced == check_tiles; by_state counts the VISIBLE assets plus the synthetic flow entries named
 *  in synthetic_entries: "unplaced" (checks that landed on no asset), and for grid.invalid (DESIGN-R3 C5) one
 *  "invalid:<asset id>" per asset the API could not place plus one "invalid" for invalid entries that name no asset (an
 *  answer older than R3 carries one "invalid" for them all). */
export interface HealthGridReconciliation {
  store_rows: number;
  excluded_rows: number;
  made_by_api: number;
  check_tiles: number;
  landed: number;
  unplaced: number;
  /** R4 D2: the per-pipeline readings in grid.unplaced (marked evidence_of) - no check tiles; an older answer has none */
  unplaced_evidence?: number;
  rerouted: number;
  invalid: number;
  assets: number;
  visible_assets: number;
  hidden_assets: number;
  by_state: { fine: number; problem: number; recovered: number; unknown: number; not_applicable: number };
  synthetic_entries: string[];
  balanced: boolean;
}

export interface HealthGridAsset {
  id: string;
  label: string;
  type: string;
  env: GridEnv;
  plane: GridPlaneId;
  band: string;
  bands: string[];
  cols: string[];
  node: string | null;
  member: string | null;
  /** set members (node members minus those with their own tile) */
  members: string[];
  state: HealthState;
  verdict: HealthVerdict | null;
  colour: HealthColour;
  role: HealthRole;
  section: HealthSection;
  hidden: boolean;
  reason: string | null;
  measured_at_utc: string | null;
  failure_class: string | null;
  collapsed_under: string[];
  unknown_cause: HealthUnknownCause | null;
  failing_since_utc: string | null;
  failing_since_is_floor: boolean;
  /** the checks that LANDED here (each check appears on exactly one asset) */
  tiles: HealthTile[];
  /** watched by (asset/node monitors plus the monitors of landed tiles) */
  monitors: string[];
  schedule_text: string | null;
  lines: { schedule: HealthGridLine; ran: HealthGridLine; complete: HealthGridLine; moved: HealthGridLine };
  /** control-plane recorded state */
  summary: HealthRecorded | null;
  facts: HealthRecorded[];
  /** DESIGN-R2 A2: per-pipeline evidence folded into this asset's state (absent in an answer older than R2 = none) */
  evidence?: HealthGridEvidence[];
}

export interface HealthGridBand {
  id: string;
  label: string;
}

export interface HealthGridRail {
  label: string;
  between: [string, string];
  from: string[];
  to: { asset: string; kind: "flow" | "jdbc" }[];
  jdbc_label: string;
}

export interface HealthGridLink {
  from: string;
  to: string;
  kind: "flow" | "jdbc";
  via: "rail" | null;
}

export interface HealthGrid {
  version: 1;
  dataflow: {
    bands: HealthGridBand[];
    columns: { id: string; segments: { from_band: string; label: string }[] }[];
    rail: HealthGridRail | null;
  };
  control: { bands: HealthGridBand[]; columns: { id: string; label: string }[] };
  /** model order */
  assets: HealthGridAsset[];
  links: HealthGridLink[];
  /** checks that landed on no asset (should be empty; drawn if not) */
  unplaced: HealthTile[];
  /** model grid entries the API could not place (drawn, never dropped). DESIGN-R3 C5: `entry` is the UNKNOWN flow entry
   *  each counts under - `invalid:<asset id>` for an asset the API could not place (`invalid:#<position>` when its id is no
   *  identifier; `#<position>` appended for a second copy of an id), else `invalid` (absent in an answer older than R3) */
  invalid: { id: string | null; why: string; entry?: string }[];
  /** DESIGN-R2 A7 - absent in an answer older than R2 (the page then says the counts cannot be reconciled: UNKNOWN) */
  reconciliation?: HealthGridReconciliation;
}

export interface HealthResponse {
  schema: "health-api/2";
  evaluated_at_utc: string;
  store: {
    location: string;
    read_ok: boolean;
    read_error: string | null;
    read_at_utc: string;
    self: HealthTile;
    entries: HealthStoreEntry[];
    excluded: string[];
  };
  model: {
    source_repo: string;
    source_path: string;
    source_commit: string;
    git_blob_sha: string;
    generated: string;
    status: string;
  };
  monitors: HealthMonitor[];
  unregistered_monitors: HealthMonitor[];
  nodes: HealthNode[];
  edges: { from: string; to: string }[];
  band: {
    /** visible DATA-section primaries + possibly_consequent - the headline number */
    problems: number;
    /** visible UNKNOWN entries of ANY section - never dropped */
    unknown: number;
    suppressed: number;
    primaries: string[];
    possibly_consequent: string[];
    colour: string;
    headline: string;
    /** visible MONITORING-section (the control plane's) primaries + possibly_consequent */
    monitoring_problems: number;
    monitoring: string[];
    /** visible HOUSEKEEPING-section entries, any state or role */
    housekeeping: number;
    housekeeping_entries: string[];
    /** visible MEASURED/RECOVERED entries, any section - never a problem, never healthy */
    recovered: number;
    recovered_entries: string[];
    /** the count of the `unwatched` cause line in unknown_causes - a subset of `unknown` */
    not_watched: number;
    unknown_causes: HealthUnknownCauseCount[];
  };
  coverage: {
    monitors_on_page: number;
    nodes_total: number;
    uncovered_nodes: string[];
    excluded_monitors: { id: string; why: string }[];
    monitor_to_nodes: Record<string, string[]>;
    node_to_monitors: Record<string, string[]>;
  };
  grid: HealthGrid;
}
